import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { moduleConfig } from './config';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../../services/paginatedList';

export type MgaSheetColumnType = 'EARNING' | 'DEDUCTION' | 'INFO';
export type MgaSheetColumnBehavior = 'MANUAL' | 'CARRY_FORWARD' | 'DERIVED' | 'REQUEST_MIRROR';
export type MgaPostingBucket = 'ALLOTMENT' | 'CASH_ADVANCE' | 'BONDED' | 'DEDUCTIONS';

export interface MgaSheetColumn {
  id: number;
  code: string;
  name: string;
  columnType: MgaSheetColumnType;
  dataType: 'DECIMAL' | 'INTEGER';
  behavior: MgaSheetColumnBehavior;
  includeInTotal: boolean;
  sortOrder: number;
  isActive: boolean;
  showInGrid: boolean;
  sourceKey?: string | null;
  postingBucket?: MgaPostingBucket | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
  /** True when settlement amounts exist on any MGA crew line. */
  inUse?: boolean;
}

/** System behavior or already used on MGA periods — cannot delete. */
export function isLockedSheetColumn(row: Pick<MgaSheetColumn, 'behavior' | 'inUse'>) {
  return row.behavior !== 'MANUAL' || row.inUse === true;
}

export function sheetColumnLockReason(row: Pick<MgaSheetColumn, 'behavior' | 'inUse'>) {
  if (row.behavior !== 'MANUAL') {
    return 'System column — used by payroll and cannot be deleted';
  }
  if (row.inUse) {
    return 'In use on MGA periods — cannot be deleted';
  }
  return null;
}

export const useMgaSheetColumns = (
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<
    PaginatedApiResponse<MgaSheetColumn> | MgaSheetColumn[]
  >(`${moduleConfig.module.api.list}${q}`, fetcher, { revalidateOnFocus: true });
  const normalized = normalizePaginatedListResponse<MgaSheetColumn>(data, page, limit);
  return {
    data: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useMgaSheetColumn = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<MgaSheetColumn>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
};
